import React, { useState, useEffect } from 'react';
import {
  Flex,
  Typography,
  Button,
  Card,
  Table,
  Tag,
  Avatar,
  Tabs,
  Spin,
  Skeleton,
  Row,
  Col,
  Segmented,
  Pagination,
  Space
} from 'antd';
import {
  ArrowLeftOutlined,
  UserOutlined,
  ShopOutlined,
  TeamOutlined,
  CarOutlined,
  AppstoreOutlined,
  UnorderedListOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import DashboardService from '../../service';
import LotCard from '../../components/LotCard';
import VinCopy from '../../components/VinCopy';
import CreatorInfo from '../../components/CreatorInfo';

const BranchDetail = () => {
  const { branchId, lang } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [branch, setBranch] = useState(null);
  const [managers, setManagers] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [subdealers, setSubdealers] = useState([]);
  const [purchasedCars, setPurchasedCars] = useState([]);
  const [activeTab, setActiveTab] = useState('0');
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bidViewMode, setBidViewMode] = useState('grouped');
  const [isBidsGrouped, setIsBidsGrouped] = useState(false);
  const [bidsLoading, setBidsLoading] = useState(false);

  const [managersPagination, setManagersPagination] = useState({});
  const [dealersPagination, setDealersPagination] = useState({});
  const [subdealersPagination, setSubdealersPagination] = useState({});
  const [managersPage, setManagersPage] = useState(1);
  const [dealersPage, setDealersPage] = useState(1);
  const [subdealersPage, setSubdealersPage] = useState(1);
  const [paginationLoading, setPaginationLoading] = useState(false);

  const loadManagers = async (page = 1) => {
    if (!branch) return;
    try {
      setPaginationLoading(true);
      const managersResponse = await DashboardService.getUsersByRole('manager', { branchId: branch.id }, page, 100);
      if (managersResponse.success && managersResponse.users) {
        setManagers(managersResponse.users);
        setManagersPagination(managersResponse.pagination || {});
        setManagersPage(page);
      }
    } catch (error) {
      console.error('Error loading managers:', error);
    } finally {
      setPaginationLoading(false);
    }
  };

  const loadDealers = async (page = 1) => {
    if (!branch) return;
    try {
      setPaginationLoading(true);
      const dealersResponse = await DashboardService.getUsersByRole('dealer', { branchId: branch.id }, page, 100);
      if (dealersResponse.success && dealersResponse.users) {
        setDealers(dealersResponse.users);
        setDealersPagination(dealersResponse.pagination || {});
        setDealersPage(page);
      }
    } catch (error) {
      console.error('Error loading dealers:', error);
    } finally {
      setPaginationLoading(false);
    }
  };

  const loadSubdealers = async (page = 1) => {
    if (!branch) return;
    try {
      setPaginationLoading(true);
      const subdealersResponse = await DashboardService.getUsersByRole('subdealer', { branchId: branch.id }, page, 100);
      if (subdealersResponse.success && subdealersResponse.users) {
        setSubdealers(subdealersResponse.users);
        setSubdealersPagination(subdealersResponse.pagination || {});
        setSubdealersPage(page);
      }
    } catch (error) {
      console.error('Error loading subdealers:', error);
    } finally {
      setPaginationLoading(false);
    }
  };

  const loadBranchBids = async () => {
    if (!branchId) return;
    try {
      setBidsLoading(true);
      const groupByLot = bidViewMode === 'grouped';
      const response = await DashboardService.getBranchBids(branchId, {
        page: 1,
        limit: 50,
        groupByLot
      });
      if (response.success !== false) {
        const bidsData = response.lots || response.bids || [];
        setBids(bidsData);
        setIsBidsGrouped(response.grouped || false);
      }
    } catch (error) {
      console.error('Error loading branch bids:', error);
      setBids([]);
    } finally {
      setBidsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === '3' && branchId) {
      loadBranchBids();
    }
  }, [bidViewMode, activeTab]);

  useEffect(() => {
    const loadBranchData = async () => {
      try {
        setLoading(true);
        const branchResponse = await DashboardService.getBranchById(branchId);
        if (branchResponse.success && branchResponse.branch) {
          setBranch(branchResponse.branch);
        }

        const currentBranchId = branchResponse.branch?.id;
        if (currentBranchId) {
          const [managersRes, dealersRes, subdealersRes] = await Promise.all([
            DashboardService.getUsersByRole('manager', { branchId: currentBranchId }, 1, 100),
            DashboardService.getUsersByRole('dealer', { branchId: currentBranchId }, 1, 100),
            DashboardService.getUsersByRole('subdealer', { branchId: currentBranchId }, 1, 100)
          ]);

          if (managersRes.success && managersRes.users) {
            setManagers(managersRes.users);
            setManagersPagination(managersRes.pagination || {});
          }
          if (dealersRes.success && dealersRes.users) {
            setDealers(dealersRes.users);
            setDealersPagination(dealersRes.pagination || {});
          }
          if (subdealersRes.success && subdealersRes.users) {
            setSubdealers(subdealersRes.users);
            setSubdealersPagination(subdealersRes.pagination || {});
          }

          try {
            const purchasedLotsResponse = await DashboardService.getBranchPurchasedLots(currentBranchId, { page: 1, limit: 1000 });
            if (purchasedLotsResponse?.success && purchasedLotsResponse.lots) {
              setPurchasedCars(purchasedLotsResponse.lots);
            } else if (purchasedLotsResponse?.lots) {
              setPurchasedCars(purchasedLotsResponse.lots);
            }
          } catch (e) {
            console.error('Error loading purchased lots:', e);
          }

          await loadBranchBids();
        }
      } catch (error) {
        console.error('Error loading branch data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (branchId) {
      loadBranchData();
    }
  }, [branchId]);

  const handleBack = () => {
    navigate(`/${lang}/branches`);
  };

  const handleManagerClick = (manager) => {
    const managerId = manager.managerId || manager.autoIncrementId || manager.id;
    if (managerId) {
      navigate(`/${lang}/managers/${managerId}`);
    }
  };

  const handleDealerClick = (dealer) => {
    const dealerId = dealer.dealerId || dealer.autoIncrementId || dealer.id;
    if (dealerId) {
      navigate(`/${lang}/dealers/${dealerId}`);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'won': return 'success';
      case 'active': return 'blue';
      case 'lost': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 24 }}>
        <Skeleton active paragraph={{ rows: 2 }} style={{ marginBottom: 24 }} />
        <Flex gap={16} style={{ marginBottom: 24 }}>
          <Skeleton.Avatar active size={64} />
          <div style={{ flex: 1 }}>
            <Skeleton.Input active block style={{ marginBottom: 8 }} />
            <Skeleton.Input active block size="small" />
          </div>
        </Flex>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          {[1, 2, 3, 4].map((i) => (
            <Col xs={24} sm={12} md={6} key={i}>
              <Card><Skeleton active /></Card>
            </Col>
          ))}
        </Row>
        <Skeleton.Button active block style={{ marginBottom: 16 }} />
        <Card><Skeleton active paragraph={{ rows: 5 }} /></Card>
      </div>
    );
  }

  if (!branch) {
    return (
      <div style={{ padding: 24, textAlign: 'center', marginTop: 48 }}>
        <Typography.Title level={4} type="danger">Branch not found</Typography.Title>
        <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
          The requested branch could not be found or you don't have access to it.
        </Typography.Text>
        <Button type="primary" icon={<ArrowLeftOutlined />} onClick={handleBack}>
          Back to Branches
        </Button>
      </div>
    );
  }

  const stats = [
    { title: 'Total Managers', value: managersPagination.totalUsers || managers.length, icon: <UserOutlined />, color: '#1e3a5f' },
    { title: 'Total Dealers', value: dealersPagination.totalUsers || dealers.length, icon: <ShopOutlined />, color: '#22c55e' },
    { title: 'Total Subdealers', value: subdealersPagination.totalUsers || subdealers.length, icon: <TeamOutlined />, color: '#f59e0b' },
    { title: 'Purchased Cars', value: purchasedCars.length, icon: <CarOutlined />, color: '#8b5cf6' }
  ];

  const managerColumns = [
    {
      title: 'Manager',
      key: 'manager',
      render: (_, record) => (
        <Flex align="center" gap={8}>
          <Avatar style={{ backgroundColor: '#1e3a5f' }}>{record.avatar}</Avatar>
          <div>
            <div style={{ fontWeight: 600 }}>{record.name}</div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>@{record.user_name}</Typography.Text>
          </div>
        </Flex>
      )
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_, record) => (
        <div>
          <div>{record.phone}</div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>{record.email}</Typography.Text>
        </div>
      )
    },
    {
      title: 'Dealers',
      key: 'dealers',
      render: (_, record) => (
        <Tag icon={<ShopOutlined />} color="blue">
          {dealers.filter(d => d.managerId === record.id).length}
        </Tag>
      )
    },
    {
      title: 'Subdealers',
      key: 'subdealers',
      render: (_, record) => (
        <Tag icon={<TeamOutlined />}>{record.stats?.totalSubdealers || 0}</Tag>
      )
    },
    {
      title: 'Active Bids',
      dataIndex: ['stats', 'activeBids'],
      key: 'activeBids',
      render: (v) => v || 0
    },
    {
      title: 'Monthly Revenue',
      dataIndex: ['stats', 'monthlyRevenue'],
      key: 'monthlyRevenue',
      render: (v) => <span style={{ fontWeight: 600 }}>{v || '$0'}</span>
    }
  ];

  const dealerColumns = [
    {
      title: 'Dealer',
      key: 'dealer',
      render: (_, record) => (
        <Flex align="center" gap={8}>
          <Avatar style={{ backgroundColor: '#1e3a5f' }}>{record.avatar}</Avatar>
          <div>
            <div style={{ fontWeight: 600 }}>{record.name}</div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>@{record.user_name}</Typography.Text>
          </div>
        </Flex>
      )
    },
    { title: 'Manager', dataIndex: 'managerName', key: 'managerName' },
    {
      title: 'Contact',
      key: 'contact',
      render: (_, record) => (
        <div>
          <div>{record.phone}</div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>{record.email}</Typography.Text>
        </div>
      )
    },
    {
      title: 'Subdealers',
      key: 'subdealers',
      render: (_, record) => (
        <Tag icon={<TeamOutlined />}>{record.stats?.totalSubdealers || 0}</Tag>
      )
    },
    {
      title: 'Active Bids',
      dataIndex: ['stats', 'activeBids'],
      key: 'activeBids',
      render: (v) => v || 0
    },
    {
      title: 'Total Spent',
      dataIndex: ['stats', 'totalSpent'],
      key: 'totalSpent',
      render: (v) => <span style={{ fontWeight: 600 }}>{v || '$0'}</span>
    }
  ];

  const subdealerColumns = [
    {
      title: 'Subdealer',
      key: 'subdealer',
      render: (_, record) => (
        <Flex align="center" gap={8}>
          <Avatar style={{ backgroundColor: '#8b5cf6' }}>{record.avatar}</Avatar>
          <div>
            <div style={{ fontWeight: 600 }}>{record.name}</div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>@{record.user_name}</Typography.Text>
          </div>
        </Flex>
      )
    },
    { title: 'Dealer', dataIndex: 'dealerName', key: 'dealerName' },
    { title: 'Manager', dataIndex: 'managerName', key: 'managerName' },
    {
      title: 'Contact',
      key: 'contact',
      render: (_, record) => (
        <div>
          <div>{record.phone}</div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>{record.email}</Typography.Text>
        </div>
      )
    },
    {
      title: 'Active Bids',
      dataIndex: ['stats', 'activeBids'],
      key: 'activeBids',
      render: (v) => v || 0
    },
    {
      title: 'Won Auctions',
      dataIndex: ['stats', 'wonAuctions'],
      key: 'wonAuctions',
      render: (v) => v || 0
    },
    {
      title: 'Total Spent',
      dataIndex: ['stats', 'totalSpent'],
      key: 'totalSpent',
      render: (v) => <span style={{ fontWeight: 600 }}>{v || '$0'}</span>
    }
  ];

  const bidColumns = [
    {
      title: 'Lot Number',
      dataIndex: 'lotNumber',
      key: 'lotNumber',
      render: (v) => <span style={{ fontWeight: 600 }}>{v}</span>
    },
    {
      title: 'VIN',
      key: 'vin',
      render: (_, record) => (
        <Flex align="center" gap={4}>
          <Typography.Text type="secondary">{record.vin || 'N/A'}</Typography.Text>
          {record.vin && <VinCopy vin={record.vin} size="small" />}
        </Flex>
      )
    },
    {
      title: 'Bid Amount',
      key: 'bidAmount',
      render: (_, record) => (
        <span style={{ fontWeight: 600, color: '#1e3a5f' }}>
          ${((record.bidAmount || record.bid) || 0).toLocaleString()}
        </span>
      )
    },
    {
      title: 'User',
      dataIndex: 'appUserName',
      key: 'user',
      render: (v) => v || 'Unknown'
    },
    {
      title: 'Event',
      dataIndex: 'event',
      key: 'event',
      render: (v) => v ? <Tag>{v}</Tag> : 'N/A'
    },
    {
      title: 'Time',
      dataIndex: 'createdAt',
      key: 'time',
      render: (v) => v ? new Date(v).toLocaleString() : 'N/A'
    }
  ];

  const tabItems = [
    {
      key: '0',
      label: `Managers (${managersPagination.totalUsers || managers.length})`,
      children: (
        <div style={{ position: 'relative' }}>
          {paginationLoading && (
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(255,255,255,0.7)', zIndex: 1
            }}>
              <Spin size="large" />
            </div>
          )}
          <Table
            columns={managerColumns}
            dataSource={managers}
            rowKey={r => r.id || r.user_id}
            pagination={false}
            onRow={(record) => ({
              onClick: () => handleManagerClick(record),
              style: { cursor: 'pointer' }
            })}
            locale={{ emptyText: 'No managers found' }}
          />
          {managersPagination.totalUsers > 0 && (
            <Flex justify="center" align="center" style={{ marginTop: 16 }} vertical gap={8}>
              {managersPagination.totalPages > 1 && (
                <Pagination
                  total={managersPagination.totalUsers}
                  current={managersPage}
                  pageSize={100}
                  onChange={loadManagers}
                  disabled={paginationLoading}
                />
              )}
              <Typography.Text type="secondary">
                Showing {managers.length} of {managersPagination.totalUsers} total
              </Typography.Text>
            </Flex>
          )}
        </div>
      )
    },
    {
      key: '1',
      label: `Dealers (${dealersPagination.totalUsers || dealers.length})`,
      children: (
        <div style={{ position: 'relative' }}>
          {paginationLoading && (
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(255,255,255,0.7)', zIndex: 1
            }}>
              <Spin size="large" />
            </div>
          )}
          <Table
            columns={dealerColumns}
            dataSource={dealers}
            rowKey={r => r.id || r.user_id}
            pagination={false}
            onRow={(record) => ({
              onClick: () => handleDealerClick(record),
              style: { cursor: 'pointer' }
            })}
            locale={{ emptyText: 'No dealers found' }}
          />
          {dealersPagination.totalUsers > 0 && (
            <Flex justify="center" align="center" style={{ marginTop: 16 }} vertical gap={8}>
              {dealersPagination.totalPages > 1 && (
                <Pagination
                  total={dealersPagination.totalUsers}
                  current={dealersPage}
                  pageSize={100}
                  onChange={loadDealers}
                  disabled={paginationLoading}
                />
              )}
              <Typography.Text type="secondary">
                Showing {dealers.length} of {dealersPagination.totalUsers} total
              </Typography.Text>
            </Flex>
          )}
        </div>
      )
    },
    {
      key: '2',
      label: `Subdealers (${subdealersPagination.totalUsers || subdealers.length})`,
      children: (
        <div style={{ position: 'relative' }}>
          {paginationLoading && (
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(255,255,255,0.7)', zIndex: 1
            }}>
              <Spin size="large" />
            </div>
          )}
          <Table
            columns={subdealerColumns}
            dataSource={subdealers}
            rowKey={r => r.id || r.user_id}
            pagination={false}
            locale={{ emptyText: 'No subdealers found' }}
          />
          {subdealersPagination.totalUsers > 0 && (
            <Flex justify="center" align="center" style={{ marginTop: 16 }} vertical gap={8}>
              {subdealersPagination.totalPages > 1 && (
                <Pagination
                  total={subdealersPagination.totalUsers}
                  current={subdealersPage}
                  pageSize={100}
                  onChange={loadSubdealers}
                  disabled={paginationLoading}
                />
              )}
              <Typography.Text type="secondary">
                Showing {subdealers.length} of {subdealersPagination.totalUsers} total
              </Typography.Text>
            </Flex>
          )}
        </div>
      )
    },
    {
      key: '3',
      label: 'Bids & Cars',
      children: (
        <div>
          <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
            <Typography.Title level={5} style={{ margin: 0 }}>Branch Bids</Typography.Title>
            <Segmented
              value={bidViewMode}
              onChange={setBidViewMode}
              options={[
                { value: 'grouped', label: <><AppstoreOutlined /> Grouped by Lot</> },
                { value: 'flat', label: <><UnorderedListOutlined /> Flat List</> }
              ]}
            />
          </Flex>

          {bidsLoading ? (
            <Flex justify="center" style={{ padding: 48 }}>
              <Spin size="large" />
            </Flex>
          ) : isBidsGrouped && bidViewMode === 'grouped' ? (
            <Space direction="vertical" style={{ width: '100%' }}>
              {bids.length > 0 ? (
                bids.map((lot, index) => (
                  <LotCard key={lot.lotNumber || index} lot={lot} />
                ))
              ) : (
                <Card>
                  <Typography.Text type="secondary">No bids found for this branch</Typography.Text>
                </Card>
              )}
            </Space>
          ) : (
            <Table
              columns={bidColumns}
              dataSource={bids}
              rowKey={(r, i) => r._id || i}
              pagination={false}
              locale={{ emptyText: 'No bids found for this branch' }}
            />
          )}
        </div>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack} style={{ marginBottom: 16 }}>
          Back to Branches
        </Button>

        <Flex align="center" gap={16} style={{ marginBottom: 16 }}>
          <Avatar size={64} style={{ backgroundColor: '#1e3a5f', fontSize: 24 }}>
            {branch.flag}
          </Avatar>
          <div>
            <Typography.Title level={3} style={{ margin: 0 }}>{branch.name}</Typography.Title>
            <Typography.Text type="secondary">{branch.location} • {branch.code}</Typography.Text>
          </div>
        </Flex>

        {(branch.createdBy || branch.createdAt) && (
          <div style={{ marginTop: 16 }}>
            <CreatorInfo createdBy={branch.createdBy} createdAt={branch.createdAt} />
          </div>
        )}
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {stats.map((stat, index) => (
          <Col xs={24} sm={12} md={6} key={index}>
            <Card size="small">
              <Flex align="center" gap={8}>
                <span style={{ color: stat.color, fontSize: 20 }}>{stat.icon}</span>
                <div>
                  <Typography.Title level={4} style={{ margin: 0 }}>{stat.value}</Typography.Title>
                  <Typography.Text type="secondary">{stat.title}</Typography.Text>
                </div>
              </Flex>
            </Card>
          </Col>
        ))}
      </Row>

      <Card style={{ marginBottom: 16 }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      </Card>
    </div>
  );
};

export default BranchDetail;
